import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useState, useMemo } from 'react';
import { Header } from '../components/layout/Header';
import { BottomNav } from '../components/layout/BottomNav';
import { EventCard } from '../components/events/EventCard';
import { EventEmptyState } from '../components/events/EventEmptyState';
import { useEventMockStore } from '../store/useEventMockStore';
import { FiArrowLeft } from 'react-icons/fi';

export const Route = createFileRoute('/events/yours')({
  beforeLoad: ({ location }) => {
    if (!localStorage.getItem('access_token')) {
      throw redirect({ to: '/sign-in', search: { redirect: location.pathname } });
    }
  },
  component: YourEventsPage,
});

type TabKey = 'all' | 'drafts' | 'published';

function YourEventsPage() {
  const [activeTab, setActiveTab] = useState<TabKey>('all');
  const [visibleCount, setVisibleCount] = useState(8);
  const navigate = useNavigate();

  const events = useEventMockStore((s) => s.events);
  const myEvents = useMemo(() => events.filter((e) => e.event_creator_id === 1), [events]);

  const filteredEvents = useMemo(() => {
    switch (activeTab) {
      case 'drafts':
        return myEvents.filter((e) => e.is_draft_or_published === 'draft');
      case 'published':
        return myEvents.filter((e) => e.is_draft_or_published === 'published' && !e.is_cancelled);
      default:
        return myEvents.filter((e) => !e.is_cancelled);
    }
  }, [myEvents, activeTab]);

  const displayedEvents = filteredEvents.slice(0, visibleCount);
  const hasMore = filteredEvents.length > visibleCount;

  const tabs: { key: TabKey; label: string; count: number }[] = [
    { key: 'all', label: 'All Events', count: myEvents.filter((e) => !e.is_cancelled).length },
    {
      key: 'drafts',
      label: 'Drafts',
      count: myEvents.filter((e) => e.is_draft_or_published === 'draft').length,
    },
    {
      key: 'published',
      label: 'Published',
      count: myEvents.filter((e) => e.is_draft_or_published === 'published' && !e.is_cancelled)
        .length,
    },
  ];

  return (
    <div className="min-h-screen bg-black text-white pb-32">
      <Header />

      <main className="pt-24 md:pt-20 max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back + Title */}
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => window.history.back()}
            className="p-2 hover:bg-white/5 rounded-full transition-colors group"
          >
            <FiArrowLeft
              size={22}
              className="text-zinc-400 group-hover:text-white transition-colors"
            />
          </button>
          <h1 className="text-white text-2xl sm:text-3xl font-bold">Your Events</h1>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 mb-8 border-b border-white/10 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => {
                setActiveTab(tab.key);
                setVisibleCount(8);
              }}
              className={`relative px-5 py-3 text-sm font-medium transition-colors ${
                activeTab === tab.key ? 'text-white' : 'text-zinc-500 hover:text-zinc-300'
              }`}
            >
              <span className="flex items-center gap-2">
                {tab.label}
                <span
                  className={`inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full text-[11px] font-bold ${
                    activeTab === tab.key ? 'bg-white text-black' : 'bg-zinc-800 text-zinc-400'
                  }`}
                >
                  {tab.count}
                </span>
              </span>
              {activeTab === tab.key && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-white rounded-full" />
              )}
            </button>
          ))}
        </div>

        {/* Grid */}
        {filteredEvents.length === 0 ? (
          <EventEmptyState tab={activeTab} />
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {displayedEvents.map((event) => (
                <EventCard
                  key={event.id}
                  event={event}
                  onClick={() =>
                    navigate({
                      to: '/events/$eventId',
                      params: { eventId: String(event.id) },
                    })
                  }
                />
              ))}
            </div>

            {/* Load More */}
            {hasMore && (
              <div className="flex justify-center mt-8 mb-8">
                <button
                  onClick={() => setVisibleCount((prev) => prev + 8)}
                  className="px-10 py-3 rounded-full border border-white/20 text-white text-sm font-medium hover:bg-white/5 transition-all active:scale-95"
                >
                  Load More
                </button>
              </div>
            )}
          </>
        )}
      </main>

      <BottomNav />
    </div>
  );
}
